/**
 * summer-sale-cart.js
 * -------------------
 * Optional upsell mechanic for the cart drawer.
 *
 * When a trigger product is in the cart, this shows a picker offering
 * a 2nd item at a discount (e.g. 50% OFF) with variant swatches.
 *
 * Key differences from free-gift-cart.js:
 *  - Completely optional — never blocks checkout.
 *  - Customer can dismiss the offer.
 *  - Items are added with _summer_sale_item property for tracking.
 *  - The actual discount is applied by a Shopify automatic discount,
 *    not by this script. We only display the calculated sale price.
 */

(function () {
  'use strict';

  // ─── Constants ──────────────────────────────────────────────────────────────

  const SALE_PROPERTY   = '_summer_sale_item';
  const SALE_FOR_PROP   = '_summer_sale_for';
  const PICKER_ID       = 'cart-sale-upsell';
  const DISMISS_KEY     = 'ssu_dismissed';

  // ─── Helpers ────────────────────────────────────────────────────────────────

  function getConfig() {
    const el = document.getElementById('summer-sale-rules-data');
    if (!el) return null;
    try { return JSON.parse(el.textContent); }
    catch (e) { console.error('[SummerSale] Could not parse rules JSON', e); return null; }
  }

  function money(cents) {
    if (typeof window.Shopify !== 'undefined' && window.Shopify.money_format) {
      return window.Shopify.money_format.replace('{{amount}}', (cents / 100).toFixed(2));
    }
    return '$' + (cents / 100).toFixed(2);
  }

  function fetchCart() {
    return fetch('/cart.js', {
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    }).then(function (r) { return r.json(); });
  }

  function getCartDrawer() {
    return document.querySelector('cart-drawer');
  }

  function getSections() {
    var drawer = getCartDrawer();
    return drawer ? drawer.getSectionsToRender().map(function (s) { return s.id || s.section; }) : [];
  }

  function rerenderDrawer(sectionsData) {
    var drawer = getCartDrawer();
    if (!drawer || !sectionsData) return;
    drawer.renderContents({ sections: sectionsData });
    document.dispatchEvent(new CustomEvent('shopify:section:load'));
  }

  function escapeHtml(str) {
    if (str == null) return '';
    return String(str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function colorNameToHex(name) {
    var map = {
      black:'#1a1a1a', white:'#f5f5f5', red:'#dc2626', blue:'#2563eb',
      green:'#16a34a', yellow:'#eab308', pink:'#ec4899', purple:'#9333ea',
      orange:'#ea580c', grey:'#9ca3af', gray:'#9ca3af', brown:'#92400e',
      navy:'#1e3a5f', beige:'#d4b896', cream:'#fef3c7', gold:'#d97706',
      silver:'#c0c0c0', tan:'#d2b48c', olive:'#808000', coral:'#ff6b6b',
      teal:'#0d9488', mint:'#6ee7b7', lavender:'#c4b5fd', rose:'#f43f5e',
    };
    return map[(name || '').toLowerCase().trim()] || '#e5e7eb';
  }

  // ─── Dismissal tracking (sessionStorage) ─────────────────────────────────

  function isDismissed(ruleKey) {
    try {
      var dismissed = JSON.parse(sessionStorage.getItem(DISMISS_KEY) || '{}');
      return !!dismissed[ruleKey];
    } catch (e) { return false; }
  }

  function setDismissed(ruleKey) {
    try {
      var dismissed = JSON.parse(sessionStorage.getItem(DISMISS_KEY) || '{}');
      dismissed[ruleKey] = true;
      sessionStorage.setItem(DISMISS_KEY, JSON.stringify(dismissed));
    } catch (e) { /* no-op */ }
  }

  // ─── Eligibility check ──────────────────────────────────────────────────────

  /**
   * Check if trigger product is in cart (excluding any sale-upsell lines).
   */
  function hasTrigger(rule, cartItems) {
    return cartItems.some(function (item) {
      return item.product_id === rule.trigger_id &&
        !(item.properties && item.properties[SALE_PROPERTY]);
    });
  }

  /**
   * Check if an upsell item has already been added via this picker.
   */
  function hasUpsellItem(rule, cartItems) {
    return cartItems.some(function (item) {
      return item.properties &&
        item.properties[SALE_PROPERTY] &&
        item.properties[SALE_FOR_PROP] === rule.trigger_handle;
    });
  }

  // ─── Picker rendering ────────────────────────────────────────────────────────

  function renderPicker(config, cartItems) {
    var container = document.getElementById(PICKER_ID);
    if (!container) return;

    var eligibleRules = config.rules.filter(function (r) {
      if (!r.trigger_id || !r.upsell_id) return false;
      if (!hasTrigger(r, cartItems)) return false;
      if (hasUpsellItem(r, cartItems)) return false;
      if (isDismissed(r.trigger_handle + ':' + r.upsell_id)) return false;
      return true;
    });

    if (eligibleRules.length === 0) {
      if (container.style.display !== 'none') {
        container.style.display = 'none';
        container.innerHTML = '';
        container.removeAttribute('data-picker-state');
      }
      return;
    }

    var stateKey = eligibleRules
      .map(function (r) { return r.upsell_id + ':' + r.trigger_id; })
      .join('|');

    container.style.setProperty('--ssu-accent', config.accent_color || '#EA580C');
    container.style.display = '';

    if (container.dataset.pickerState === stateKey) return;
    container.dataset.pickerState = stateKey;

    container.innerHTML = eligibleRules
      .map(function (rule) { return renderRuleRow(rule, config); })
      .join('');

    eligibleRules.forEach(function (rule) { attachSwatchHover(rule); });
  }

  function renderRuleRow(rule, config) {
    var discountAmount = (config.discount_amount || 50) * 100; // convert dollars to cents
    var discountLabel = config.discount_label || '$50 OFF';
    var ctaText = config.cta_text || 'Add to cart — $50 OFF';
    var heading = config.heading || 'Flash Sale: Get $50 OFF a 2nd Travel Bundle!';

    var price = rule.upsell_price || 0;
    var salePrice = Math.max(0, price - discountAmount);

    var imgSrc = rule.upsell_featured_image
      ? rule.upsell_featured_image + (rule.upsell_featured_image.includes('?') ? '&' : '?') + 'width=280'
      : null;
    var imgHtml = imgSrc
      ? '<img src="' + imgSrc + '" alt="' + escapeHtml(rule.upsell_title || '') + '" class="ssu-pick-image" width="112" height="112" loading="lazy">'
      : '';

    var priceHtml = '<div class="ssu-price-line">' +
      '<span class="ssu-discount-pill">' + escapeHtml(discountLabel) + '</span>' +
      '<span class="ssu-price-sale">' + money(salePrice) + '</span>' +
      '<span class="ssu-price-original"><s>' + money(price) + '</s></span>' +
      '</div>';

    var productName = rule.upsell_title
      ? '<span class="ssu-product-name">' + escapeHtml(rule.upsell_title) + '</span>'
      : '';

    var controlHtml = '';
    if (rule.upsell_has_only_default_variant || !rule.upsell_variants || rule.upsell_variants.length <= 1) {
      controlHtml = '<button type="button" class="ssu-add-btn" ' +
        'data-rule-upsell-id="' + rule.upsell_id + '" ' +
        'aria-label="Add item to cart">' + escapeHtml(ctaText) + '</button>';
    } else {
      var swatches = rule.upsell_variants.map(function (v) {
        var imgUrl = v.image
          ? v.image + (v.image.includes('?') ? '&' : '?') + 'width=80'
          : null;
        var bgStyle = imgUrl
          ? "background-image:url('" + imgUrl + "');background-size:cover;background-position:center;"
          : 'background-color:' + colorNameToHex(v.option1 || v.title) + ';';

        var salePriceV = Math.max(0, (v.price || price) - discountAmount);

        return '<button type="button" ' +
          'class="ssu-swatch-btn' + (!v.available ? ' ssu-swatch-btn--unavailable' : '') + '" ' +
          'data-variant-id="' + v.id + '" ' +
          'data-rule-upsell-id="' + rule.upsell_id + '" ' +
          'data-variant-title="' + escapeHtml(v.title) + '" ' +
          'data-variant-price="' + (v.price || price) + '" ' +
          'data-variant-sale-price="' + salePriceV + '" ' +
          (!v.available ? 'disabled aria-disabled="true" ' : '') +
          'aria-label="' + escapeHtml(v.title) + '" ' +
          'title="' + escapeHtml(v.title) + '" ' +
          'style="' + bgStyle + '"' +
          '></button>';
      }).join('');

      controlHtml = '<div class="ssu-swatch-row">' +
        '<span class="ssu-variant-label">Pick your color: </span>' +
        '<span class="ssu-swatch-selected-name"></span>' +
        '</div>' +
        '<div class="ssu-variant-picker" data-rule-upsell-id="' + rule.upsell_id + '">' +
        swatches +
        '</div>';
    }

    return '<div class="ssu-box" data-rule-upsell-id="' + rule.upsell_id + '" data-rule-trigger="' + escapeHtml(rule.trigger_handle) + '">' +
      '<button type="button" class="ssu-dismiss" aria-label="Dismiss offer" data-dismiss-key="' + escapeHtml(rule.trigger_handle + ':' + rule.upsell_id) + '">' +
        '<svg width="10" height="10" viewBox="0 0 14 14" fill="none"><path d="M1 1l12 12M13 1L1 13" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>' +
      '</button>' +
      '<div class="ssu-left">' +
        '<svg class="ssu-sale-icon" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
          '<path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z"></path>' +
          '<line x1="7" y1="7" x2="7.01" y2="7"></line>' +
        '</svg>' +
        '<div class="ssu-text">' +
          '<p class="ssu-heading">' + escapeHtml(heading) + '</p>' +
          priceHtml +
          productName +
          controlHtml +
        '</div>' +
      '</div>' +
      imgHtml +
    '</div>';
  }

  function attachSwatchHover(rule) {
    var container = document.getElementById(PICKER_ID);
    if (!container) return;
    var picker = container.querySelector('.ssu-variant-picker[data-rule-upsell-id="' + rule.upsell_id + '"]');
    if (!picker) return;
    var nameEl = picker.previousElementSibling ? picker.previousElementSibling.querySelector('.ssu-swatch-selected-name') : null;
    if (!nameEl) return;

    picker.querySelectorAll('.ssu-swatch-btn:not([disabled])').forEach(function (btn) {
      btn.addEventListener('mouseenter', function () {
        nameEl.textContent = btn.dataset.variantTitle || '';
      });
      btn.addEventListener('mouseleave', function () {
        var sel = picker.querySelector('.ssu-swatch-btn--selected');
        nameEl.textContent = sel ? sel.dataset.variantTitle || '' : '';
      });
    });
  }

  // ─── Event delegation ────────────────────────────────────────────────────────

  function setupPickerDelegation(config) {
    var container = document.getElementById(PICKER_ID);
    if (!container || container._ssuDelegated) return;
    container._ssuDelegated = true;

    container.addEventListener('click', function (e) {
      // ── Dismiss button ──
      var dismissBtn = e.target.closest('.ssu-dismiss');
      if (dismissBtn) {
        e.stopPropagation();
        var key = dismissBtn.dataset.dismissKey;
        if (key) setDismissed(key);
        var box = dismissBtn.closest('.ssu-box');
        if (box) {
          box.style.display = 'none';
          // Check if all boxes are hidden
          var visibleBoxes = container.querySelectorAll('.ssu-box:not([style*="display: none"])');
          if (visibleBoxes.length === 0) {
            container.style.display = 'none';
          }
        }
        return;
      }

      // ── Swatch button ──
      var swatchBtn = e.target.closest('.ssu-swatch-btn');
      if (swatchBtn) {
        if (swatchBtn.disabled || swatchBtn.classList.contains('ssu-swatch-btn--unavailable')) return;
        e.stopPropagation();

        var variantId = parseInt(swatchBtn.dataset.variantId, 10);
        var upsellId  = parseInt(swatchBtn.dataset.ruleUpsellId, 10);
        if (!variantId || !upsellId) return;

        // Visual feedback — select swatch
        var picker = swatchBtn.closest('.ssu-variant-picker');
        if (picker) {
          picker.querySelectorAll('.ssu-swatch-btn').forEach(function (b) {
            b.classList.remove('ssu-swatch-btn--selected');
          });
          swatchBtn.classList.add('ssu-swatch-btn--selected');
          var nameEl = picker.previousElementSibling ? picker.previousElementSibling.querySelector('.ssu-swatch-selected-name') : null;
          if (nameEl) nameEl.textContent = swatchBtn.dataset.variantTitle || '';

          // Update price display if variant has different price
          var box = swatchBtn.closest('.ssu-box');
          if (box && swatchBtn.dataset.variantSalePrice) {
            var salePriceEl = box.querySelector('.ssu-price-sale');
            var origPriceEl = box.querySelector('.ssu-price-original s');
            if (salePriceEl) salePriceEl.textContent = money(parseInt(swatchBtn.dataset.variantSalePrice, 10));
            if (origPriceEl) origPriceEl.textContent = money(parseInt(swatchBtn.dataset.variantPrice, 10));
          }
        }

        // Check if there's already an add button, if not, add one
        var box = swatchBtn.closest('.ssu-box');
        var existingBtn = box ? box.querySelector('.ssu-add-btn') : null;
        if (!existingBtn && box) {
          var textArea = box.querySelector('.ssu-text');
          if (textArea) {
            var addBtn = document.createElement('button');
            addBtn.type = 'button';
            addBtn.className = 'ssu-add-btn';
            addBtn.dataset.ruleUpsellId = upsellId;
            addBtn.setAttribute('aria-label', 'Add item to cart');
            addBtn.textContent = config.cta_text || 'Add to cart — $50 OFF';
            textArea.appendChild(addBtn);
          }
        }
        return;
      }

      // ── Add button ──
      var addBtn = e.target.closest('.ssu-add-btn');
      if (addBtn) {
        if (addBtn.disabled || addBtn.classList.contains('loading')) return;
        e.stopPropagation();

        var upsellId = parseInt(addBtn.dataset.ruleUpsellId, 10);
        var rule = config.rules.find(function (r) { return r.upsell_id === upsellId; });
        if (!rule) return;

        // Find selected variant
        var box = addBtn.closest('.ssu-box');
        var selectedSwatch = box ? box.querySelector('.ssu-swatch-btn--selected') : null;
        var variantId;

        if (selectedSwatch) {
          variantId = parseInt(selectedSwatch.dataset.variantId, 10);
        } else if (rule.upsell_has_only_default_variant || !rule.upsell_variants || rule.upsell_variants.length <= 1) {
          variantId = rule.upsell_variants && rule.upsell_variants[0] ? rule.upsell_variants[0].id : null;
        } else {
          // No variant selected — flash the swatch row
          var swatchRow = box ? box.querySelector('.ssu-swatch-row') : null;
          if (swatchRow) {
            swatchRow.style.color = '#DC2626';
            swatchRow.querySelector('.ssu-variant-label').textContent = 'Please pick a color first: ';
            setTimeout(function () {
              swatchRow.style.color = '';
              swatchRow.querySelector('.ssu-variant-label').textContent = 'Pick your color: ';
            }, 2000);
          }
          return;
        }

        if (!variantId) return;

        addBtn.disabled = true;
        addBtn.classList.add('loading');
        addBtn.textContent = 'Adding...';
        addUpsellToCart(variantId, rule, config);
      }
    });
  }

  // ─── Add upsell item to cart ────────────────────────────────────────────────

  function addUpsellToCart(variantId, rule, config) {
    var drawer   = getCartDrawer();
    var sections = getSections();

    var properties = {};
    properties[SALE_PROPERTY] = true;
    properties[SALE_FOR_PROP] = rule.trigger_handle || '';

    fetch((window.Shopify.routes.root || '/') + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        id: variantId,
        quantity: 1,
        properties: properties,
        sections: sections,
        sections_url: window.location.pathname,
      }),
    })
      .then(function (r) { return r.json(); })
      .then(function (response) {
        if (response.status) {
          console.error('[SummerSale] Failed to add upsell:', response.description);
          refreshPicker(config);
          return;
        }
        if (response.sections && drawer) {
          rerenderDrawer(response.sections);
        }
        refreshPicker(config);
      })
      .catch(function (e) {
        console.error('[SummerSale] addUpsellToCart error', e);
        refreshPicker(config);
      });
  }

  // ─── Sync: remove upsell items if trigger is removed ────────────────────────

  var _isSyncing = false;

  async function syncUpsellItems(config, cartItems) {
    if (!config || !config.enabled) return false;

    var drawer   = getCartDrawer();
    var sections = getSections();
    var changesMade = false;

    for (var i = 0; i < config.rules.length; i++) {
      var rule = config.rules[i];
      if (!hasTrigger(rule, cartItems)) {
        // Trigger removed — remove any upsell items for this rule
        var upsellLines = cartItems.filter(function (item) {
          return item.properties &&
            item.properties[SALE_PROPERTY] &&
            item.properties[SALE_FOR_PROP] === rule.trigger_handle;
        });

        if (upsellLines.length === 0) continue;

        _isSyncing = true;
        try {
          for (var j = 0; j < upsellLines.length; j++) {
            changesMade = true;
            try {
              var res = await fetch((window.Shopify.routes.root || '/') + 'cart/change.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  id: upsellLines[j].key,
                  quantity: 0,
                  sections: sections,
                  sections_url: window.location.pathname,
                }),
              });
              var data = await res.json();
              if (data.sections && drawer) {
                var tempDiv = document.createElement('div');
                tempDiv.innerHTML = data.sections[drawer.sectionId] || '';
                var newBody = tempDiv.querySelector('.drawer__body');
                var currentBody = drawer.querySelector('.drawer__body');
                if (newBody && currentBody) {
                  currentBody.innerHTML = newBody.innerHTML;
                }
                if (data.sections['cart-icon-bubble']) {
                  var iconEl = document.getElementById('cart-icon-bubble');
                  if (iconEl) {
                    var iconDiv = document.createElement('div');
                    iconDiv.innerHTML = data.sections['cart-icon-bubble'];
                    var newIcon = iconDiv.querySelector('#cart-icon-bubble');
                    if (newIcon) iconEl.innerHTML = newIcon.innerHTML;
                  }
                }
              }
            } catch (e) {
              console.error('[SummerSale] sync error', e);
            }
          }
        } finally {
          _isSyncing = false;
        }
      }
    }

    return changesMade;
  }

  // ─── Main refresh ─────────────────────────────────────────────────────────────

  async function refreshPicker(config) {
    if (!config || !config.enabled) return;

    var cart      = await fetchCart();
    var cartItems = cart.items || [];

    var changesMade = await syncUpsellItems(config, cartItems);
    if (changesMade) return;

    setupPickerDelegation(config);
    renderPicker(config, cartItems);
  }

  // ─── Observe cart drawer mutations ───────────────────────────────────────────

  function observeDrawerRerenders(config) {
    var drawer = getCartDrawer();
    if (!drawer) return;

    var debounceTimer;
    var observer = new MutationObserver(function (mutations) {
      var picker = document.getElementById(PICKER_ID);
      var isOwnUpdate = mutations.every(function (m) {
        var t = m.target;
        return (t === picker || (picker && picker.contains(t)));
      });
      if (isOwnUpdate) return;

      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(function () {
        if (document.getElementById(PICKER_ID)) refreshPicker(config);
      }, 200);
    });

    observer.observe(drawer, { childList: true, subtree: true });
  }

  // ─── Fetch interceptor ─────────────────────────────────────────────────────

  function interceptCartFetch(config) {
    var _originalFetch = window.fetch;
    window.fetch = function () {
      var args = Array.prototype.slice.call(arguments);
      var result = _originalFetch.apply(this, args);

      if (_isSyncing) return result;

      var url = typeof args[0] === 'string' ? args[0] : (args[0] && args[0].url) || '';
      if (url.includes('/cart/change') || url.includes('/cart/update')) {
        result
          .then(function (r) { return r.clone().json(); })
          .then(function () {
            setTimeout(function () { refreshPicker(config); }, 400);
          })
          .catch(function () {});
      }
      return result;
    };
  }

  // ─── Bootstrap ───────────────────────────────────────────────────────────────

  function init() {
    var config = getConfig();
    if (!config || !config.enabled || !config.rules || config.rules.length === 0) return;

    config.rules = config.rules.filter(function (r) { return r.trigger_id && r.upsell_id; });
    if (config.rules.length === 0) return;

    interceptCartFetch(config);

    // Refresh when cart icon is clicked
    document.addEventListener('click', function (e) {
      if (e.target.closest('#cart-icon-bubble, #cart-icon-bubble-mobile')) {
        setTimeout(function () { refreshPicker(config); }, 100);
      }
    });

    // Refresh when drawer opens
    var drawer = getCartDrawer();
    if (drawer) {
      new MutationObserver(function (mutations) {
        mutations.forEach(function (m) {
          if (m.type === 'attributes' && m.attributeName === 'class') {
            if (drawer.classList.contains('active')) {
              setTimeout(function () { refreshPicker(config); }, 50);
            }
          }
        });
      }).observe(drawer, { attributes: true });
    }

    observeDrawerRerenders(config);

    if (document.getElementById(PICKER_ID)) refreshPicker(config);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
